import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'

import { BackContinue } from '@components/back_continue/back_continue'
import { RoomSelector } from '@components/room_selector/room_selector'
import { useBookingContext } from '@providers/booking/booking_context'
import { buildPetTabGroups } from '@shared/booking_groups'
import { isEmpty } from '@shared/common'
import { MAX_WIDGET_SIZE, STICKY_FOOTER_HEIGHT } from '@shared/constants'
import { i18WithParams as t } from '@shared/locale'
import { hasGroomingServiceType } from '@shared/offers'
import { addonGroupsByCategory } from '@shared/pet_addons'
import { BoardingIcon } from 'components-ui/src/asset/boarding_icon'
import { BoneIcon } from 'components-ui/src/asset/bone_icon'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { useRedirectNoData } from 'hooks/use_redirect_no_data'
import { AddOnItem } from './addon_item_picker/addon_item_picker'
import { Grooms } from './grooming/grooms'

export const SearchPetAddOns = () => {
  const bookingCtx = useBookingContext()
  const [currentTabIndex, setTabIndex] = useState(0)
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const onChangeTab = useCallback((_label: string, index: number) => setTabIndex(index), [])
  const hasGroomingEnabled = hasGroomingServiceType(bookingCtx)

  // redirect to home if the state has invalid data
  useRedirectNoData()

  useEffect(() => {
    if (bookingCtx.state.currentState !== 'petAddOns') {
      bookingCtx.dispatch({ type: 'SET_SEARCH_STATE', payload: { value: 'petAddOns' } })
      bookingCtx.dispatch({ type: 'RESET_GROOM_WIZARD_STATE' })
    }
  })

  const addonGroups = useMemo(
    () => bookingCtx.state.data.petsAddOns && addonGroupsByCategory(bookingCtx.state.data.petsAddOns),
    [bookingCtx.state.data.petsAddOns]
  )

  const addOnsToRender = useMemo(() => {
    return addonGroups?.order
      .map((og) => {
        const currentPetGroupOfferId = bookingCtx.state.cart.groups?.[currentTabIndex]?.offerSelected?.offer?.id
        const itemsToRender = addonGroups.items[og].filter((item) =>
          item.offer?.associatedOffers?.some(({ parentOfferId }) => parentOfferId === currentPetGroupOfferId)
        )
        return itemsToRender.length ? (
          <Fragment key={isEmpty(og) ? '_empty_' : og}>
            <Box sx={{ maxWidth: MAX_WIDGET_SIZE, margin: '0 auto' }}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
                <>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <BoardingIcon />
                    <Typography variant="h5">{isEmpty(og) ? t('searchPetAddOns.genericTitle') : og}</Typography>
                  </Stack>
                  <Spacer height={1} />

                  {!isBelowMD ? <Spacer height={3} /> : <Spacer height={1} />}
                  {isBelowMD && <Divider orientation="horizontal" sx={{ my: 2 }} />}

                  <Box sx={{ maxWidth: '1000px', margin: '0 auto' }}>
                    {itemsToRender.map((o) => {
                      return (
                        <Fragment key={o.offer!.id!}>
                          <AddOnItem addOn={o} groupIdx={currentTabIndex} kind="petAddOn" />
                          <Divider orientation="horizontal" sx={{ my: 2 }} />
                        </Fragment>
                      )
                    })}
                  </Box>
                </>
              </Paper>
              <Spacer height={3} />
            </Box>
          </Fragment>
        ) : null
      })
      .filter((a) => !!a)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTabIndex])

  const tabs = buildPetTabGroups(bookingCtx.state.cart.groups)

  useEffect(() => {
    document.title = t('title.addPetPerks')
  }, [])

  const onContinueHandler = useCallback(async (): Promise<boolean> => {
    if (tabs.length > 1 && currentTabIndex != tabs.length - 1) {
      setTabIndex(currentTabIndex + 1)
      return false
    }
    return true
  }, [currentTabIndex, tabs.length])

  return (
    <Box sx={{ maxWidth: MAX_WIDGET_SIZE, margin: '0 auto' }}>
      <Spacer height={3} />
      <>
        <Stack direction="row" alignItems="center">
          <BoneIcon color="primary" sx={{ width: 40, height: 40 }} />
          <Typography variant="h2">{t('searchPetAddOns.title')}</Typography>
        </Stack>
        <Typography variant="body1" sx={{ px: 2, paddingLeft: 5 }}>
          {t('searchPetAddOns.summary')}
        </Typography>
        {tabs.length > 1 && (
          <>
            <Spacer height={3} />
            <RoomSelector tabIndex={currentTabIndex} tabs={tabs} onChange={onChangeTab} />
          </>
        )}

        <Spacer height={3} />

        {hasGroomingEnabled && <Grooms currentTabIndex={currentTabIndex} />}

        {addOnsToRender?.length ? (
          addOnsToRender
        ) : (
          <Box sx={{ maxWidth: MAX_WIDGET_SIZE, margin: '0 auto' }}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
              <Typography variant="body1">
                {t('booking.petAddOns.emptyMessage', { petNames: tabs[currentTabIndex]?.label })}
              </Typography>
            </Paper>
          </Box>
        )}

        <Spacer height={STICKY_FOOTER_HEIGHT} />
        <BackContinue onContinue={onContinueHandler} />
      </>
    </Box>
  )
}
